import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import { get, compact } from 'lodash-es';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { VehicleEntities } from 'client/data/models/vehicle-v2';
import { buildReviewPath, EditorialReviewModel, EditorialReviewEntities } from 'client/data/models/editorial-review';
import {
  buildMarketAnalysisPath,
  buildWhatsNewPath,
  VehicleDetailsEntities,
  VehicleDetailsModel,
} from 'client/data/models/mmy-details';
import { buildLatestVideosForGenerationPath, MediaModel } from 'client/data/models/media';
import { buildBuyingTipsPath, BuyingTipsModel } from 'client/data/models/buying-tips';
import { CorePageParams, getParamsFromVehicle } from 'site-modules/shared/utils/core-page/params';
import {
  addRatingsV4NamesAndQuestions,
  getModelSectionMap,
  KEY_TO_HASH,
} from 'site-modules/shared/utils/core-page/editorial-review';
import { MENU_HASH } from 'site-modules/shared/constants/core-page/menu-hash';
import { GLOBAL_HEADER_OFFSET } from 'site-modules/shared/constants/sub-navigation';
import { AnimatedSticky } from 'site-modules/shared/components/animated-sticky/animated-sticky';
import { AnchorNavContent } from 'site-modules/shared/components/anchor-nav-sticky/anchor-nav-content';

import './new-core-subnav.scss';

export function NewCoreSubnavUI({
  params,
  vehicle,
  className = null,
  linkClassName = null,
  isMobile = false,
  isBuyingTipsEnabled = false,
  isMainContentWidth = false,
  editorialReview = null,
  whatsNewContent = null,
  hasVideos = false,
  hasBuyingTips = false,
  hasMarketAnalysis = false,
  ...restProps
}) {
  const props = {
    ...restProps,
    editorialReview,
    whatsNewContent,
    hasVideos,
    hasBuyingTips,
    hasMarketAnalysis,
  };

  const ratingScorecardSections = compact(
    (
      addRatingsV4NamesAndQuestions({ sections: props?.editorialReview?.ratingsV4?.ratingSections, params }) ||
      getModelSectionMap({
        edmundsTypeCategories: vehicle.edmundsTypeCategories,
        params,
        isScorecardV4: props?.editorialReview?.isScorecardV4,
      })
    ).filter(
      ({ key, score }) =>
        Object.keys(KEY_TO_HASH).includes(key) && (score || props?.editorialReview?.ratings?.[key]?.rating)
    )
  );

  const SUBNAV_ITEMS = compact([
    {
      name: 'Pricing',
      hash: isMobile ? MENU_HASH.TRIM_SUMMARY_MOBILE : MENU_HASH.TRIM_SUMMARY_DESKTOP,
      customOffset: isMobile ? undefined : GLOBAL_HEADER_OFFSET,
    },
    {
      name: 'Overview',
      hash: MENU_HASH.MODEL_OVERVIEW,
      dataPath: ['editorialReview.summary'],
    },
    { name: 'Compare', hash: MENU_HASH.COMPETITION },
    { name: 'Specs', hash: MENU_HASH.FEATURES_SPECS },
    {
      name: 'Pros & Cons',
      hash: MENU_HASH.PROS_CONS,
      dataPath: ['editorialReview.pros.length', 'editorialReview.cons.length'],
    },
    {
      name: "What's New",
      hash: MENU_HASH.WHATS_NEW,
      dataPath: ['whatsNewContent.length'],
    },
    (params.isElectric || params.isPluginHybrid) && { name: 'EV Insights', hash: MENU_HASH.EV_INSIGHTS },
    { name: 'Verdict', hash: MENU_HASH.VERDICT, dataPath: ['editorialReview.ratings.overall'] },
    ...ratingScorecardSections?.map?.(({ name, key }) => ({ name, hash: KEY_TO_HASH[key] })),
    { name: 'Trim Pricing', hash: MENU_HASH.PRICING },
    isBuyingTipsEnabled && {
      name: 'Buying Tips',
      hash: MENU_HASH.BUYING_TIPS,
      dataPath: ['hasBuyingTips', 'hasMarketAnalysis'],
    },
    {
      name: 'Trims',
      hash: MENU_HASH.TRIM_DESCRIPTIONS,
      dataPath: ['editorialReview.trimFeaturesBodySnippet'],
    },
    { name: 'Owner Reviews', hash: MENU_HASH.CONSUMER_REVIEWS },
    { name: 'Videos', hash: MENU_HASH.VIDEOS, dataPath: ['hasVideos'] },
    { name: 'Reliability', hash: MENU_HASH.RELIABILITY },
    !params.isCore7591Enabled && { name: 'Price Checker', hash: MENU_HASH.PRICE_CHECKER },
    { name: 'Safety', hash: MENU_HASH.SAFETY },
    { name: 'FAQs', hash: MENU_HASH.FAQS },
    !(params.isElectric || params.isPluginHybrid) && { name: 'Cost To Drive', hash: MENU_HASH.COST_TO_DRIVE },
  ]);

  const anchorNavContentProps = {
    navList: SUBNAV_ITEMS.filter(({ dataPath }) => !dataPath || dataPath.some(path => get(props, path))),
    activeClass: 'link-active heading-5',
    listClassName: 'd-flex gap-1 gap-md-2 ms-0',
    linkClassName: classnames('pt-1 pb-0_75', linkClassName),
    arrowClassName: classnames(
      'icon btn btn-link border-0 text-decoration-none text-primary-darker h-100 px-0_5 px-md-0_75',
      'size-16',
      linkClassName
    ),
    itemClassName: 'size-16',
    useNoFollow: false,
    isHashTracking: true,
  };

  const stickyFragment = (
    <AnchorNavContent
      {...anchorNavContentProps}
      className="sticky border-0"
      linkClassName={undefined}
      arrowClassName={undefined}
      linkTrackingId="view_content_docked"
      isSticky
    />
  );

  return (
    <AnimatedSticky
      stickyFragment={
        isMainContentWidth ? (
          <Row>
            <Col xs={12} lg={8}>
              {stickyFragment}
            </Col>
          </Row>
        ) : (
          stickyFragment
        )
      }
      identifierClass={classnames('new-core-subnav', className)}
      creativeId="edm-entry-sub-nav"
    >
      <AnchorNavContent {...anchorNavContentProps} linkTrackingId="view_content_non_docked" />
    </AnimatedSticky>
  );
}

NewCoreSubnavUI.propTypes = {
  params: CorePageParams.isRequired,
  vehicle: VehicleEntities.MakeModelSubmodelYear.isRequired,
  className: PropTypes.string,
  linkClassName: PropTypes.string,
  editorialReview: EditorialReviewEntities.EditorialReview,
  whatsNewContent: VehicleDetailsEntities.WhatsNewContent,
  hasVideos: PropTypes.bool,
  hasBuyingTips: PropTypes.bool,
  hasMarketAnalysis: PropTypes.bool,
  isBuyingTipsEnabled: PropTypes.bool,
  isMainContentWidth: PropTypes.bool,
  isMobile: PropTypes.bool,
};

const mapStateToProps = state => ({
  isBuyingTipsEnabled: !!state.featureFlags?.buyingTips,
});

export const NewCoreSubnav = connect(mapStateToProps)(
  connectToModel(NewCoreSubnavUI, {
    editorialReview: bindToPath(({ vehicle }) => buildReviewPath(getParamsFromVehicle(vehicle)), EditorialReviewModel),
    whatsNewContent: bindToPath(({ params }) => buildWhatsNewPath(params), VehicleDetailsModel),
    hasVideos: bindToPath(
      ({ vehicle }) =>
        buildLatestVideosForGenerationPath({ make: vehicle.make.slug, model: vehicle.model.slug, year: vehicle.year }),
      MediaModel,
      videos => !!videos?.filter?.(video => video?.videoId)?.length
    ),
    hasBuyingTips: bindToPath(
      ({ vehicle }) => buildBuyingTipsPath(getParamsFromVehicle(vehicle)),
      BuyingTipsModel,
      buyingTips => !!buyingTips?.formattedText
    ),
    hasMarketAnalysis: bindToPath(
      ({ params, isBuyingTipsEnabled }) => isBuyingTipsEnabled && buildMarketAnalysisPath(params),
      VehicleDetailsModel,
      marketAnalysis => !!marketAnalysis?.text
    ),
  })
);
